<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
  <div data-samora>
    <transition name="fade">
      <div
        v-if="isOpen"
        class="sam-modal__overlay"
        :style="overlayStyleObject"
        @click="handleOverlayClick()"
      />
    </transition>
    <transition
      name="fade"
      @before-enter="beforeModalOpen()"
      @enter="modalOpening()"
      @before-leave="beforeModalClose()"
      @after-leave="afterModalClose()"
    >
      <div
        v-if="isOpen"
        ref="modal"
        class="sam-modal shadow--modal"
        :class="modalClassObject"
        role="dialog"
        tabindex="0"
        aria-modal="true"
      >
        <!-- @slot Adding content to this slot will override the others-->
        <slot></slot>

        <div
          v-if="!$slots.default"
          ref="modalSlots"
          class="sam-modal__content"
        >
          <div class="sam-modal__header">
            <h5
              class="sam-modal__title"
              :title="title"
            >
              {{ title }}
            </h5>
          </div>
          <div
            v-if="$slots.body"
            class="sam-modal__body"
          >
            <!-- @slot Slot to store the main components inside the modal -->
            <slot name="body" />
          </div>
          <div
            v-if="$slots.footer"
            class="sam-modal__footer"
          >
            <!-- @slot Slot to place action buttons that will always stay at the bottom of the modal -->
            <slot name="footer" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import SAMORA_SIZES from '../../../utils/constants/samora-sizes.enum';
import { lockScroll, unlockScroll } from '../../../utils/scrollLock';

let previouslyFocusedElement;

export const MODAL_SIZES = {
  SMALL: SAMORA_SIZES.SMALL,
  MEDIUM: SAMORA_SIZES.MEDIUM,
  LARGE: SAMORA_SIZES.LARGE,
};

export default {
  name: 'SamModal',
  props: {
    /**
     * If the modal is open or not
     *
     * @values true, false
     */
    isOpen: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * The modal title
     */
    title: {
      type: String,
      default: undefined,
      required: false,
    },
    /**
     * Whether the modal can be closed with ESCAPE key or clicking outside of its area
     *
     * @values true, false
     */
    canDismiss: {
      type: Boolean,
      default: true,
      required: false,
    },
    /**
     * Whether the modal should occupy the full screen on mobile
     *
     * @values true, false
     */
    isFullScreenOnMobile: {
      type: Boolean,
      default: true,
      required: false,
    },
    /**
     * The modal size
     * @values sm, md, lg
     */
    size: {
      type: String,
      default: SAMORA_SIZES.MEDIUM,
      required: false,
      validator: (size) => Object.values(MODAL_SIZES).includes(size),
    },
  },
  computed: {
    overlayStyleObject() {
      return {
        cursor: this.canDismiss ? 'pointer' : null,
      };
    },
    modalClassObject() {
      return {
        'is-full-screen-on-mobile': this.isFullScreenOnMobile,
        'is-small': this.size === SAMORA_SIZES.SMALL,
        'is-large': this.size === SAMORA_SIZES.LARGE,
      };
    },
  },
  mounted() {
    if (!this.isOpen) {
      return;
    }

    lockScroll();
    this.focusModal();
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  destroyed() {
    unlockScroll();
  },
  methods: {
    beforeModalOpen() {
      previouslyFocusedElement = document.activeElement;
      lockScroll();
    },
    modalOpening() {
      this.focusModal();
      document.addEventListener('keydown', this.handleKeyDown);
    },
    beforeModalClose() {
      document.removeEventListener('keydown', this.handleKeyDown);

      if (previouslyFocusedElement) {
        previouslyFocusedElement.focus();
      }
    },
    afterModalClose() {
      unlockScroll();
    },
    /**
     * This method is incomplete and a very naive way of getting focusable elements.
     * Will do for now but as Unbabel grows we might need to use a more mature solution like:
     * https://github.com/focus-trap/tabbable
     */
    getFocusableElements() {
      const focusableQuery =
        'button:enabled:not(.is-icon), [href], input:enabled, select:enabled, textarea:enabled, [contenteditable], [tabindex]:not([tabindex="-1"]):not([disabled])';
      return Array.from(this.$refs.modal.querySelectorAll(focusableQuery));
    },
    focusModal() {
      const autofocusChild = this.$refs.modal.querySelector('[autofocus]');

      if (autofocusChild) {
        autofocusChild.focus();
        return;
      }

      this.$refs.modal.focus();

      // TODO: this needs to be better thought.
      // At this time PDs don't want autofocus on controls
      // but it'd be ideal to have autofocus on the first interactive element of the body (e.g. forms in modals)
      // At this time though Modals in Portal don't use the right slots so it'd be hard to identify the modal's body
      // need to refactor all Portal's modals to use body & footer slots then re-enable the code below
      // and change getFocusableElements to retrieve only from body

      // const focusableElements = this.getFocusableElements();
      //
      // if (!focusableElements.length) {
      //   this.$refs.modal.focus();
      //   return;
      // }
      //
      // const firstFocusableElement = focusableElements[0];
      // firstFocusableElement.focus();
    },
    handleOverlayClick() {
      if (!this.canDismiss) {
        return;
      }

      this.$emit('close');
    },
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.canDismiss) {
        /**
         * Triggered when the user triggered the intent to close like the ESCAPE key or outside
         * of the modal area
         */
        this.$emit('close');
        return;
      }
      /**
       * Trap focus inside Modal
       * SHIFT + TAB - focus previous element
       * TAB - focus next element
       */
      if (event.key === 'Tab') {
        const focusableElements = this.getFocusableElements();
        /**
         * If there are no focusable elements, keep the focus on the modal div
         */
        if (!focusableElements.length) {
          event.preventDefault();
          this.$refs.modal.focus();
          return;
        }
        const firstFocusableElement = focusableElements[0];
        const lastFocusableElement = focusableElements[focusableElements.length - 1];
        /**
         * If the current focus is the first focusable element and the user wants to go back
         * then let's focus on the last element
         */
        if (event.shiftKey && document.activeElement === firstFocusableElement) {
          event.preventDefault();
          // eslint-disable-next-line no-unused-expressions
          lastFocusableElement?.focus?.();
          return;
        }
        /**
         * If the current focus is the last focusable element on the modal then let's focus
         * on the first
         */
        if (!event.shiftKey && document.activeElement === lastFocusableElement) {
          event.preventDefault();
          // eslint-disable-next-line no-unused-expressions
          firstFocusableElement?.focus?.();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.sam-modal__overlay {
  @extend .elevation--2;
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgba($pure--black, 0.8);
  z-index: 1001;
}

.sam-modal {
  @extend .elevation--3;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: $pure--white;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 564px;
  max-height: 90%;
  z-index: 1001;

  > * {
    overflow-y: auto;
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }
  }

  &.is-small {
    width: 30vw;
  }

  &.is-large {
    min-width: min(50rem, 100vw);
  }

  @media only screen and (max-width: 48em) {
    &.is-full-screen-on-mobile {
      max-height: unset;
      max-width: unset;
      width: 100%;
      height: 100%;
      border-radius: unset;
    }
  }

  &__content {
    @extend .row, .px-6, .py-4;
    height: 100%;
  }

  &__title {
    color: $neutral--90;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__body {
    @extend .text--body-1, .col-xs-12;
    flex: 1;
  }

  &__footer {
    @extend .col-xs-12, .pt-4, .px-0;
  }

  &__header {
    @extend .col-xs-12, .pb-4;
  }
}
</style>
